import { NavLink } from 'react-router';
import {
  Boxes, Building2, ClipboardCheck, FileClock, Globe, KeyRound, LayoutDashboard,
  Network, RefreshCw, Server, Settings, SquareStack, Table2, Users,
} from 'lucide-react';
import { Marca } from '../componentes/Marca.js';

/**
 * Navegação principal do painel.
 *
 * Os grupos vêm do handoff. No protótipo a navegação trocava um estado interno;
 * aqui são rotas de verdade — o operador precisa poder copiar o endereço de uma
 * tela e mandar para um colega, e o botão voltar do navegador tem de funcionar.
 */

interface ItemNav {
  para: string;
  rotulo: string;
  Icone: typeof LayoutDashboard;
  /** Contador vermelho, como o de solicitações pendentes. */
  badge?: number;
}

interface GrupoNav {
  titulo: string;
  itens: ItemNav[];
}

export const GRUPOS: GrupoNav[] = [
  {
    // "Gerenciar IPs" vem primeiro depois do Dashboard de propósito: é a tela
    // de trabalho diário, onde se acha um endereço e se troca o reverso sem
    // passar por mais nada.
    titulo: 'Gestão',
    itens: [
      { para: '/', rotulo: 'Dashboard', Icone: LayoutDashboard },
      { para: '/ips', rotulo: 'Gerenciar IPs', Icone: Table2 },
      { para: '/blocos', rotulo: 'Blocos de IP', Icone: Boxes },
      { para: '/operadoras', rotulo: 'Operadoras', Icone: Building2 },
    ],
  },
  {
    // Sem "Registros PTR": era um item que abria tela vazia, porque a edição
    // de PTR pertence a um bloco e já acontece em Gerenciar IPs ou dentro do
    // bloco. Item de menu que não leva a lugar nenhum é ruído.
    titulo: 'Operação',
    itens: [
      { para: '/zonas', rotulo: 'Zonas Reverse', Icone: Globe },
      { para: '/dominios', rotulo: 'Domínios', Icone: Network },
      { para: '/solicitacoes', rotulo: 'Solicitações', Icone: ClipboardCheck },
      { para: '/templates', rotulo: 'Templates PTR', Icone: SquareStack },
    ],
  },
  {
    titulo: 'Infraestrutura',
    itens: [
      { para: '/servidores', rotulo: 'Servidores', Icone: Server },
      { para: '/sincronizacao', rotulo: 'Sincronização', Icone: RefreshCw },
      { para: '/tokens', rotulo: 'API Tokens', Icone: KeyRound },
      { para: '/logs', rotulo: 'Logs & Auditoria', Icone: FileClock },
      { para: '/usuarios', rotulo: 'Usuários & Acesso', Icone: Users },
      { para: '/configuracoes', rotulo: 'Configurações', Icone: Settings },
    ],
  },
];

interface SidebarProps {
  /** No mobile a sidebar vira gaveta; o protótipo não previa isso. */
  aberta: boolean;
  aoFechar: () => void;
  pendencias?: number;
  usuario?: { nome: string; email: string };
}

export function Sidebar({ aberta, aoFechar, pendencias = 0, usuario }: SidebarProps) {
  return (
    <>
      {/* Véu só no mobile, para fechar a gaveta tocando fora. */}
      {aberta && (
        <div
          className="fixed inset-0 z-30 bg-escuro-overlay/45 backdrop-blur-[2px] lg:hidden"
          onClick={aoFechar}
          aria-hidden="true"
        />
      )}

      <aside
        className={`fixed inset-y-0 left-0 z-40 flex w-sidebar flex-col bg-escuro
          transition-transform duration-200 lg:translate-x-0
          ${aberta ? 'translate-x-0' : '-translate-x-full'}`}
      >
        <div className="flex h-topbar shrink-0 items-center px-5">
          <Marca tamanho="pequeno" invertido />
        </div>

        <nav className="flex-1 overflow-y-auto px-3 pb-4">
          {GRUPOS.map((grupo) => (
            <div key={grupo.titulo} className="mb-5">
              <p className="mb-2 px-3 text-[10.5px] font-extrabold uppercase tracking-[0.6px] text-white/35">
                {grupo.titulo}
              </p>
              <ul className="space-y-0.5">
                {grupo.itens.map(({ para, rotulo, Icone }) => (
                  <li key={para}>
                    <NavLink
                      to={para}
                      // `end` só na raiz: sem isso o Dashboard ficaria marcado
                      // como ativo em toda rota, porque "/" prefixa tudo.
                      end={para === '/'}
                      onClick={aoFechar}
                      className={({ isActive }) =>
                        `flex items-center gap-3 rounded-[10px] px-3 py-2.5 text-[13.5px] transition-colors ${
                          isActive
                            ? 'bg-gradient-to-r from-marca to-marca-claro font-bold text-white shadow-nav-ativo'
                            : 'font-semibold text-[#a9bcd6] hover:bg-white/5 hover:text-white'
                        }`
                      }
                    >
                      <Icone size={19} strokeWidth={2} className="shrink-0" />
                      <span className="flex-1">{rotulo}</span>
                      {para === '/solicitacoes' && pendencias > 0 && (
                        <span className="rounded-pill bg-acento px-2 py-0.5 text-[11px] font-extrabold text-white">
                          {pendencias}
                        </span>
                      )}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>

        {usuario && (
          <div className="shrink-0 border-t border-white/10 p-3">
            {/* O card do usuário leva ao perfil: é onde as pessoas procuram
                por instinto, e evita mais um item na navegação. */}
            <NavLink
              to="/perfil"
              onClick={aoFechar}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-[10px] p-2 transition-colors ${
                  isActive ? 'bg-white/10' : 'hover:bg-white/5'
                }`
              }
            >
              <div className="grid size-9 shrink-0 place-items-center rounded-avatar bg-white/10 text-[13px] font-extrabold text-white">
                {iniciais(usuario.nome)}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-[13px] font-bold text-white">{usuario.nome}</p>
                <p className="truncate text-[11.5px] text-white/45">{usuario.email}</p>
              </div>
              <Settings size={15} className="shrink-0 text-white/40" />
            </NavLink>
          </div>
        )}
      </aside>
    </>
  );
}

function iniciais(nome: string): string {
  const partes = nome.trim().split(/\s+/);
  const primeira = partes[0]?.[0] ?? '';
  // Última palavra e não a segunda: "Luciano de Oliveira" deve dar LO, não LD.
  const ultima = partes.length > 1 ? (partes[partes.length - 1]?.[0] ?? '') : '';
  return (primeira + ultima).toUpperCase();
}
